/**
 * Crew Skill Index (CSI, OceanEye) — third-party micro-app.
 *
 * Unlike our own iframe micro-apps, CSI is on another domain and won't accept a
 * SIYA JWT, so the page can't just point an iframe at a static URL. It asks
 * /api/csi/embed-url for a short-lived, pre-authenticated embed URL (the server
 * does the OceanEye access-token exchange) and frames that.
 *
 * Route is /csi, so the menu record for Crew Skill Index MUST have href '/csi'.
 * Without it the page is unreachable: RouteGuard matches pathname against the
 * user's allowed hrefs and denies anything unmatched, and its only fallback is
 * for '/view/*' paths (see RouteGuard.tsx). An href of '/view/csi' would instead
 * land on the generic DB-driven /view/:id route, which frames
 * https://csi.ocean-eye.io/ directly — that gets bounced to login.ocean-eye.io
 * and blocked by that host's frame-ancestors policy, which is what this page exists
 * to avoid.
 *
 * Deliberately NOT OptimizedIframe: that component appends the SIYA authToken and
 * vessel context to the iframe URL, which would hand our JWT to a third-party origin.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, EmptyState } from '@syia-ai/maritimeai-ui';
import SimpleLayout from '@/components/SimpleLayout';
import AppLoader from '@/components/AppLoader';
import { getAuthToken } from '@/utils/authToken';
import { httpFetch } from '@/lib/httpClient';

const TITLE = 'Crew Skill Index';

export default function CsiPage() {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [frameLoaded, setFrameLoaded] = useState(false);

  const loadEmbedUrl = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setFrameLoaded(false);

    try {
      // localhost → Bearer header; deployed → null, and the HttpOnly cookie authenticates.
      const authToken = getAuthToken();
      const response = await httpFetch('/api/csi/embed-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: '{}',
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.embedUrl) {
        setError(data?.error || 'Could not start a CSI session. Please try again.');
        return;
      }

      setEmbedUrl(data.embedUrl);
    } catch {
      setError('Could not reach CSI. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // The embed URL is single-use and short-lived, so it is minted per page visit
  // rather than cached. A remount (navigating back to CSI) gets a fresh one.
  useEffect(() => {
    void loadEmbedUrl();
  }, [loadEmbedUrl]);

  if (error) {
    return (
      <SimpleLayout title={TITLE}>
        <EmptyState
          className="shell-page-state"
          headingLevel={2}
          title="Crew Skill Index didn't load"
          description={error}
          actions={<Button onClick={() => void loadEmbedUrl()}>Retry</Button>}
        />
      </SimpleLayout>
    );
  }

  return (
    <SimpleLayout title={TITLE}>
      <div className="shell-frame-box shell-frame-box--csi">
        {(isLoading || !frameLoaded) && <AppLoader fill="overlay" label={`Loading ${TITLE}`} />}

        {embedUrl && (
          <iframe
            src={embedUrl}
            title={TITLE}
            className="shell-frame-box__frame"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-downloads"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            // no-referrer, not the app-wide strict-origin-when-cross-origin: the
            // embed URL is a credential, and CSI's own sub-resource requests would
            // otherwise leak our origin to third parties on their side.
            referrerPolicy="no-referrer"
            onLoad={() => setFrameLoaded(true)}
          />
        )}
      </div>
    </SimpleLayout>
  );
}
